import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress } from '@mui/material';
import { motion, HTMLMotionProps } from 'framer-motion';
import clsx from 'clsx';
import './Button.scss';

export type ButtonIntent =
  | 'primary'      // Main CTAs: Submit, Save, Book, Create
  | 'secondary'    // Secondary actions: Learn More, View Details
  | 'success'      // Positive confirmations: Confirm, Approve
  | 'danger'       // Destructive: Delete, Remove, Disconnect
  | 'warning'      // Caution: Cancel (when work might be lost)
  | 'neutral'      // Navigation: Back, Close (when safe)
  | 'ghost'        // Minimal: tertiary actions, icon buttons

export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'size'> {
  intent?: ButtonIntent;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
  glowEffect?: boolean;
  pulseEffect?: boolean;
  iconOnly?: boolean;
  motionProps?: Omit<HTMLMotionProps<"button">, 'children'>;
}

const MotionButton = motion(MuiButton);

export const Button: React.FC<ButtonProps> = ({
  intent = 'primary',
  size = 'medium',
  loading = false,
  loadingText,
  glowEffect = false,
  pulseEffect = false,
  iconOnly = false,
  className,
  children,
  disabled,
  motionProps,
  ...rest
}) => {
  // Automatically enable glow for primary actions
  const shouldGlow = glowEffect || (intent === 'primary' && !disabled);
  // Automatically enable pulse for success actions
  const shouldPulse = pulseEffect || (intent === 'success' && !disabled);

  const buttonClasses = clsx(
    'custom-button',
    `custom-button--${intent}`,
    `custom-button--${size}`,
    {
      'custom-button--loading': loading,
      'custom-button--glow': shouldGlow,
      'custom-button--pulse': shouldPulse,
      'custom-button--icon-only': iconOnly,
    },
    className
  );

  const motionVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  // Remove any color prop that might have been passed through rest
  const { color, ...restWithoutColor } = rest as any;

  return (
    <MotionButton
      className={buttonClasses}
      disabled={disabled || loading}
      variants={motionVariants}
      initial="idle"
      whileHover={!disabled && !loading ? "hover" : "idle"}
      whileTap={!disabled && !loading ? "tap" : "idle"}
      {...motionProps}
      {...restWithoutColor}
    >
      {loading && (
        <CircularProgress
          size={size === 'small' ? 16 : size === 'medium' ? 20 : 24}
          className="custom-button__loader"
        />
      )}
      <span className="custom-button__content">
        {loading && loadingText ? loadingText : children}
      </span>
    </MotionButton>
  );
};

// Export convenience components for common use cases
export const PrimaryButton: React.FC<Omit<ButtonProps, 'intent'>> = (props) => (
  <Button intent="primary" {...props} />
);

export const DangerButton: React.FC<Omit<ButtonProps, 'intent'>> = (props) => (
  <Button intent="danger" {...props} />
);

export const GhostButton: React.FC<Omit<ButtonProps, 'intent'>> = (props) => (
  <Button intent="ghost" {...props} />
); 